/**
 * @author Vivek Kumar <vivek.kumar@314ecorp.com>
 * @description Component for Advanced Filters with Search Bar
 */
import { useEffect, useState } from 'react';
import _ from 'lodash';
import { Alert, Flex } from 'antd';
import { withErrorBoundary } from 'react-error-boundary';

import SingleFieldSearchControl from './Search';
import FilterClaimStatusData from './Filters';
import { IFieldsData } from 'lib/SearchQueryBuilder/config/entity.config';
import { getFilterQueryFromSearchParams, getSearchQueryFromFilterQuery, filterQuery as getFilteredQuery } from 'utils/advancedFilterUtils';

export type IFilterValue = {
	type?: 'search' | 'filter';
	filterParams?: AnyType;
};

interface IProps {
	initialValue: IFilterValue;
	filterSchema: IFieldsData[];
	hideFilterButton?: boolean;
	hideSearchBar?: boolean;
	onSubmit: (value: IFilterValue) => void;
	shouldResetInitialValue?: boolean;
}

/**
 * Advanced Filters with Search Bar
 */
const AdvancedFilters: React.FC<IProps> = ({
	filterSchema,
	onSubmit,
	initialValue,
	hideFilterButton,
	hideSearchBar,
	shouldResetInitialValue = false,
}) => {
	const [appliedSearch, setAppliedSearch] = useState<{ key: string; value: string } | null>(
		getSearchQueryFromFilterQuery({ searchParams: initialValue }),
	);

	useEffect(() => {
		setAppliedSearch(getSearchQueryFromFilterQuery({ searchParams: initialValue }));
	}, [shouldResetInitialValue]);

	const isSearch = !_.isEmpty(appliedSearch?.value);

	const handleSearch = ({ key, value }: { key: string; value: string }) => {
		setAppliedSearch({ key, value });
		const filterQuery = getFilterQueryFromSearchParams({
			searchParams: { key, value },
			filterSchema,
		});
		onSubmit({ type: 'search', filterParams: filterQuery });
	};

	const handleFilter = (value: AnyType) => {
		setAppliedSearch({ ...appliedSearch, value: null });
		const filteredValue = getFilteredQuery(value);
		onSubmit({ type: 'filter', filterParams: filteredValue });
	};

	return (
		<Flex gap={16}>
			{!hideSearchBar && (
				<SingleFieldSearchControl
					isSearch={isSearch}
					appliedSearch={appliedSearch}
					filterSchema={filterSchema}
					onSearch={handleSearch}
				/>
			)}
			<div className='mx-0.5 mb-0.5 h-8'>
				{!hideFilterButton && (
					<FilterClaimStatusData
						isSearch={isSearch}
						initialValue={initialValue?.filterParams}
						schema={filterSchema}
						onSubmit={handleFilter}
					/>
				)}
			</div>
		</Flex>
	);
};

export default withErrorBoundary(AdvancedFilters, {
	fallback: <Alert message='Error loading advanced filters' type='error' />,
});
